import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, CreditCard, Loader2, Gift, CheckCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const SignupForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stripe = useStripe();
  const elements = useElements();
  const logoUrl = 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingIntent, setIsLoadingIntent] = useState(false);
  const [step, setStep] = useState(1); // 1: Info, 2: Card

  // Check for referral code in URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      validateReferralCode(refCode);
    }
  }, [searchParams]);

  const validateReferralCode = async (code) => {
    if (!code || code.length < 4) {
      setReferralValid(null);
      setReferralInfo(null);
      return;
    }
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/referral/validate/${code}`);
      setReferralValid(true);
      setReferralInfo(response.data);
    } catch (error) {
      setReferralValid(false);
      setReferralInfo(null);
    }
  };

  const createSetupIntent = async () => {
    if (!formData.email || !formData.email.includes('@')) return;
    
    setIsLoadingIntent(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/users/setup-intent?email=${formData.email}`);
      setClientSecret(response.data.client_secret);
      setCustomerId(response.data.customer_id);
      setIsLoadingIntent(false);
      return true;
    } catch (error) {
      console.error('Error creating setup intent:', error);
      setIsLoadingIntent(false);
      toast({
        title: "Setup Error",
        description: "Failed to initialize payment setup. Please try again.",
        variant: "destructive"
      });
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step === 1) {
      // Validate info
      if (formData.password !== formData.confirmPassword) {
        toast({
          title: "Password Mismatch",
          description: "Passwords do not match",
          variant: "destructive"
        });
        return;
      }
      
      if (!formData.fullName || !formData.email || !formData.phone || !formData.password) {
        toast({
          title: "Missing Information",
          description: "Please fill in all fields",
          variant: "destructive"
        });
        return;
      }
      
      // Create setup intent before moving to step 2
      const success = await createSetupIntent();
      if (success) {
        setStep(2);
      }
      return;
    }

    // Step 2: Process payment method and create account
    if (!stripe || !elements) {
      toast({
        title: "Loading",
        description: "Stripe is still loading. Please wait...",
        variant: "destructive"
      });
      return;
    }

    if (!clientSecret) {
      toast({
        title: "Setup Error",
        description: "Payment setup not ready. Please go back and try again.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);

    try {
      // Confirm the SetupIntent with the card
      const cardElement = elements.getElement(CardElement);
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: formData.fullName,
            email: formData.email,
            phone: formData.phone
          }
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      // Create user account with payment method
      const signupResponse = await axios.post(`${BACKEND_URL}/api/users/signup-with-card`, {
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        setup_intent_id: setupIntent.id,
        referral_code: referralValid ? referralCode : null
      });

      let successMessage = `Card ending in ${signupResponse.data.card_last4} saved`;
      if (signupResponse.data.referral_applied) {
        successMessage += `. Referral bonus pending!`;
      }

      toast({
        title: "Account Created!",
        description: successMessage,
      });

      setTimeout(() => navigate('/user/login'), 1500);

    } catch (error) {
      console.error('Signup error:', error);
      setIsProcessing(false);
      toast({
        title: "Signup Failed",
        description: error.response?.data?.detail || error.message,
        variant: "destructive"
      });
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Button variant="ghost" onClick={() => navigate('/')} className="mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Button>
          <img src={logoUrl} alt="Logo" className="h-20 w-20 mx-auto mb-4 object-contain" />
          <h1 className="text-3xl font-bold bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent mb-2">
            Create Account
          </h1>
          <p className="text-gray-600">Join J-Style Services today</p>
        </div>

        <Card className="p-8 border-2">
          {/* Progress Indicator */}
          <div className="flex justify-center gap-2 mb-6">
            <div className={`w-2 h-2 rounded-full ${step >= 1 ? 'bg-amber-500' : 'bg-gray-300'}`} />
            <div className={`w-2 h-2 rounded-full ${step >= 2 ? 'bg-amber-500' : 'bg-gray-300'}`} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {step === 1 ? (
              <>
                <div>
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input 
                    id="fullName"
                    value={formData.fullName}
                    onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                    placeholder="John Doe"
                    required
                    className="mt-1 border-2"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input 
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="john@example.com"
                    required
                    className="mt-1 border-2"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input 
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+1 (555) 123-4567"
                    required
                    className="mt-1 border-2"
                  />
                </div>

                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input 
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="••••••••"
                    required
                    className="mt-1 border-2"
                  />
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm Password *</Label>
                  <Input 
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    placeholder="••••••••"
                    required
                    className="mt-1 border-2"
                  />
                </div>

                {/* Referral Code Section */}
                <div>
                  <Label htmlFor="referralCode" className="flex items-center gap-2">
                    <Gift size={16} className="text-amber-600" />
                    Referral Code (Optional)
                  </Label>
                  <div className="relative">
                    <Input 
                      id="referralCode"
                      value={referralCode}
                      onChange={(e) => {
                        setReferralCode(e.target.value.toUpperCase());
                        if (e.target.value.length >= 4) {
                          validateReferralCode(e.target.value);
                        } else {
                          setReferralValid(null);
                          setReferralInfo(null);
                        }
                      }}
                      placeholder="Enter code (e.g., JOHN1234)"
                      className={`mt-1 border-2 pr-10 ${
                        referralValid === true ? 'border-green-500' : 
                        referralValid === false ? 'border-red-500' : ''
                      }`}
                    />
                    {referralValid === true && (
                      <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" size={18} />
                    )}
                  </div>
                  {referralValid === true && referralInfo && (
                    <p className="text-xs text-green-600 mt-1">
                      ✓ {referralInfo.message}
                    </p>
                  )}
                  {referralValid === false && (
                    <p className="text-xs text-red-600 mt-1">
                      Invalid referral code
                    </p>
                  )}
                </div>

                <Button 
                  type="submit"
                  className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white py-6"
                  disabled={isLoadingIntent}
                >
                  {isLoadingIntent ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={16} />
                      Setting up...
                    </>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </>
            ) : (
              <>
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <CreditCard size={18} className="text-amber-600" />
                    Card Details *
                  </Label>
                  <div className="border-2 rounded-md p-3 focus-within:border-amber-500">
                    <CardElement options={cardElementOptions} />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Your card will be securely saved for faster checkout
                  </p>
                </div>

                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                  <p className="text-xs text-amber-900">
                    🔒 Your payment information is encrypted and secure. We'll save your card for convenient bookings.
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 border-2"
                    disabled={isProcessing}
                  >
                    Back
                  </Button>
                  <Button 
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white py-6"
                    disabled={isProcessing || !stripe}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={16} />
                        Creating...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </div>
              </>
            )}
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/user/login')}
                className="text-amber-600 hover:text-amber-700 font-medium"
              >
                Sign in here
              </button>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

const UserSignup = () => {
  return (
    <Elements stripe={stripePromise}>
      <SignupForm />
    </Elements>
  );
};

export default UserSignup;
